import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { FaCopy, FaDownload, FaExpand } from 'react-icons/fa';
import { downloadLabeledShareQrPng, FGTN_QR_PNG_SIZE } from '../../utils/fgtnQrCard';
import { trackShareAssetDownload } from '../../utils/enhancedAnalytics';
import FgtnQrArt from '../fgtn/FgtnQrArt';
import '../fgtn/Fgtn.css';

const PREVIEW_QR_SIZE = 240;
const LAUNCH_PREVIEW_QR_SIZE = 320;

export default function ShareQrBlock({
  heading = 'Share This Guide',
  supportingCopy = 'Scan the QR code or use the short link to open this guide on another device.',
  qrUrl,
  displayUrl,
  headline,
  supportingText,
  filename,
  fullScreenPath,
  title,
  analytics = {},
  barColor,
  bottomBarColor,
  variant = 'default',
}) {
  const qrCanvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const extraLines = [supportingText, displayUrl].filter(Boolean);

  const downloadPng = () => {
    try {
      trackShareAssetDownload({
        pageType: analytics.pageType,
        brand: analytics.brand,
        launchSeason: analytics.launchSeason,
        shortUrl: qrUrl,
        assetType: 'qr_graphic',
      });
    } catch {
      // Download must continue even if analytics fails.
    }
    downloadLabeledShareQrPng({
      qrCanvas: qrCanvasRef.current,
      headline,
      supportingText,
      displayUrl,
      extraLines,
      filename,
      barColor,
      bottomBarColor,
      variant,
    });
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
      trackShareAssetDownload({
        pageType: analytics.pageType,
        brand: analytics.brand,
        launchSeason: analytics.launchSeason,
        shortUrl: qrUrl,
        assetType: 'shortlink',
      });
    } catch (err) {
      console.error('Could not copy share URL', err);
    }
  };

  return (
    <aside className="fgtn-card fgtn-qr-card ab-launch-share-card" data-testid="share-qr-block">
      <h2>{heading}</h2>
      {supportingCopy ? <p className="ab-launch-share-copy">{supportingCopy}</p> : null}
      <FgtnQrArt
        url={qrUrl}
        size={variant === 'launch' ? LAUNCH_PREVIEW_QR_SIZE : PREVIEW_QR_SIZE}
        title={title || headline}
        headline={headline}
        supportingText={supportingText}
        displayUrl={displayUrl}
        extraLines={extraLines}
        barColor={barColor}
        bottomBarColor={bottomBarColor}
        variant={variant}
      />
      <QRCodeCanvas
        ref={qrCanvasRef}
        className="fgtn-qr-download-source"
        value={qrUrl}
        size={FGTN_QR_PNG_SIZE}
        level="H"
        marginSize={2}
        bgColor="#ffffff"
        fgColor="#111111"
      />
      <p className="ab-launch-share-url">{displayUrl || qrUrl}</p>
      <div className="fgtn-actions">
        <button type="button" className="fgtn-btn" onClick={downloadPng}>
          <FaDownload /> Download PNG
        </button>
        {fullScreenPath ? (
          <Link className="fgtn-btn-secondary" to={fullScreenPath} target="_blank" rel="noreferrer">
            <FaExpand /> Full-screen QR
          </Link>
        ) : null}
        <button type="button" className="fgtn-btn-secondary" onClick={copyUrl}>
          <FaCopy /> {copied ? 'Copied' : 'Copy short link'}
        </button>
      </div>
    </aside>
  );
}
